import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import type { IconName } from '@/components/ui/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useShell } from '@/hooks/useShell';
import { getCabinetRouteForUser } from '@/lib/roleRoutes';
import { homeNavigateOptions, readReturnTo } from '@/lib/navigationState';
import { routes } from '@/lib/routes';

const HOST_ID = 'za180-mobile-nav-host';

const ensureHost = (): HTMLElement => {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    document.body.appendChild(host);
  }
  return host;
};

type NavItem = {
  id: string;
  labelKey: 'mobile.nav.home' | 'mobile.nav.work' | 'mobile.nav.calendar' | 'mobile.nav.chat' | 'mobile.nav.profile';
  icon: IconName;
  active: (pathname: string) => boolean;
  onClick: () => void;
};

/**
 * Fixed mobile dock (HTML template `.mobile-bottom-nav`).
 * Singleton portal — one instance for the app shell.
 */
export const MobileBottomNav = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { openDrawer } = useShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setHost(ensureHost());
  }, []);

  if (!user || !host) return null;

  const onAssistant = pathname === routes.assistant;
  const items: NavItem[] = [
    {
      id: 'home',
      labelKey: 'mobile.nav.home',
      icon: 'home',
      active: (p) => p === routes.home,
      onClick: () => navigate(routes.home, homeNavigateOptions(pathname, location.state)),
    },
    {
      id: 'work',
      labelKey: 'mobile.nav.work',
      icon: 'briefcase',
      active: (p) => p.startsWith('/app') && p !== routes.assistant && !p.startsWith('/app/profile'),
      onClick: () => {
        const returnTo = readReturnTo();
        if (returnTo) {
          navigate(returnTo);
          return;
        }
        if (user) {
          navigate(getCabinetRouteForUser(user));
          return;
        }
        openDrawer();
      },
    },
    {
      id: 'calendar',
      labelKey: 'mobile.nav.calendar',
      icon: 'calendar',
      active: (p) => p.startsWith(routes.journals) || p.includes('calendar'),
      onClick: () => navigate(routes.journals),
    },
    {
      id: 'chat',
      labelKey: 'mobile.nav.chat',
      icon: 'chat',
      active: (p) => p === routes.assistant,
      onClick: () => navigate(routes.assistant),
    },
    {
      id: 'profile',
      labelKey: 'mobile.nav.profile',
      icon: 'user',
      active: (p) => p.startsWith('/app/profile'),
      onClick: () => navigate(routes.profileSection('account')),
    },
  ];

  return createPortal(
    <nav
      className={`mobile-bottom-nav${onAssistant ? ' is-route-hidden' : ''}`}
      aria-label={t('mobile.nav.label')}
      aria-hidden={onAssistant}
      hidden={onAssistant}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={item.active(pathname) ? 'active' : undefined}
          onClick={item.onClick}
        >
          <Icon name={item.icon} size={21} strokeWidth={1.8} />
          <span>{t(item.labelKey)}</span>
        </button>
      ))}
    </nav>,
    host,
  );
};
